import React, { FC } from 'react'
import { usePaginationFragment, graphql } from 'react-relay'

import {
  Text,
  Stack,
  Grid,
  Column,
  GuildCard,
  Button,
  EventItem,
  HorizontalLine,
  useBreakpoint,
} from '@components'

import { INITIAL_COUNTS } from './UserGuildMembershipsScreen'

import { UserGuildMembershipsOrganizersSection_user$key } from './__generated__/UserGuildMembershipsOrganizersSection_user.graphql'

export const UserGuildMembershipsOrganizersSection: FC<{
  user: UserGuildMembershipsOrganizersSection_user$key
}> = ({ user }) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment UserGuildMembershipsOrganizersSection_user on User
      @refetchable(
        queryName: "UserGuildMembershipsOrganizersSectionPaginationQuery"
      )
      @argumentDefinitions(
        count: { type: "Int!", defaultValue: 6 }
        cursor: { type: "Cursor" }
      ) {
        organizingGuilds: guildMemberships(
          first: $count
          after: $cursor
          condition: { membershipLevel: ORGANIZER }
        )
          @connection(
            key: "UserGuildMembershipsOrganizersSection_user_organizingGuilds"
          ) {
          edges {
            node {
              guild {
                ...GuildCard_guild
              }
            }

            cursor
          }
        }
      }
    `,
    user
  )

  const breakpoint = useBreakpoint()

  if (!data.organizingGuilds.edges.length) {
    return null
  }

  if (breakpoint === 0) {
    return (
      <Stack gap={4}>
        <Stack direction='horizontal' gap={4} alignItems='center'>
          <Text size={4} variant='cardHeading'>
            Organizing Guilds
          </Text>

          <HorizontalLine variant='cardHeading' />
        </Stack>

        <Stack gap={6}>
          {data.organizingGuilds.edges.map((edge) => (
            <GuildCard
              key={edge.cursor}
              guild={edge.node.guild}
              limitDescriptionLines={3}
            />
          ))}
        </Stack>

        {hasNext && (
          <Button
            variant='secondary'
            hoverVariant='secondaryHover'
            onPress={() => {
              if (isLoadingNext) {
                return
              }

              loadNext(3)
            }}
          >
            <Text variant='buttonSecondary' size={4}>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Text>
          </Button>
        )}
      </Stack>
    )
  }

  const guildGroups = data.organizingGuilds.edges.reduce(
    (memo, guild, index) => {
      if (index % INITIAL_COUNTS[breakpoint] === 0) {
        memo.push([guild])
      } else {
        memo[memo.length - 1].push(guild)
      }

      return memo
    },
    []
  )

  return (
    <Stack gap={4}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Organizing Guilds
        </Text>

        <HorizontalLine variant='cardHeading' />
      </Stack>

      <Stack gap={6}>
        {guildGroups.map((group, index) => {
          return (
            <Grid key={index} gap={4}>
              {group.map((edge) => {
                return (
                  <Column key={edge.cursor}>
                    <GuildCard
                      guild={edge.node.guild}
                      limitDescriptionLines={3}
                      expand
                    />
                  </Column>
                )
              })}

              {Array.from({
                length: INITIAL_COUNTS[breakpoint] - group.length,
              }).map((_, index) => (
                <Column key={index} />
              ))}
            </Grid>
          )
        })}
      </Stack>

      {hasNext && (
        <Button
          variant='secondary'
          hoverVariant='secondaryHover'
          onPress={() => {
            if (isLoadingNext) {
              return
            }

            loadNext(3)
          }}
        >
          <Text variant='buttonSecondary' size={4}>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Text>
        </Button>
      )}
    </Stack>
  )
}
